import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomTooltip from '@components/CustomTooltip';
import SetStyleSubCategory from '@components/SetStyleSubCategory';

import CollectionTableTags from '@features/collection/details/CollectionTableTags';
import ProductRemarks from '@features/collection/details/ProductRemarks';

import { storeProductData } from '@store/actions/DesignAction';

import {
	_getKey,
	changeDateFormat,
	getDateDifference,
	getProductStatusWiseColor,
	getShortName,
	groupByKey,
	onGetLocalFromUtc,
	renderSetImages
} from '@utils/Util';

const CollectionDetailsTableView = ({
	productList,
	productWiseTags,
	fetchAllTags,
	updateTagList,
	sort,
	setDesignIdToView,
	getGroupKeyName,
	SORTING_TYPE = []
}) => {
	const selectedProductIds = useSelector((store) => store.product.selectedProductIds);
	const dispatch = useDispatch();

	const toggleSelect = async (productId) => {
		let tmpSelectedProductIds = [...selectedProductIds];
		if (selectedProductIds.includes(productId)) {
			tmpSelectedProductIds = selectedProductIds.filter((id) => id !== productId);
		} else {
			tmpSelectedProductIds.push(productId);
		}
		await dispatch(storeProductData('selectedProductIds', tmpSelectedProductIds));
	};

	const onSelectAll = async (e, products) => {
		const ids = getProductsWitBasicInfoUpdated(products);
		if (e.target.checked) {
			const selectedIdList = [...new Set([...selectedProductIds, ...ids])];
			await dispatch(storeProductData('selectedProductIds', selectedIdList));
		} else {
			const modifiedIds = selectedProductIds.filter((item) => !ids.includes(item));
			await dispatch(storeProductData('selectedProductIds', modifiedIds));
		}
	};

	const renderGroupWiseTableView = () => {
		if (SORTING_TYPE.includes(sort)) {
			let groupedProducts = groupByKey(productList, 'groupByKeyName', sort);
			return Object.keys(groupedProducts)?.map((key) => {
				let products = groupedProducts[key];

				return (
					<React.Fragment key={`group_products_${key}`}>
						<h3 className="semibold-16">
							{getGroupKeyName(key)} ({groupedProducts[key]?.length})
						</h3>
						{renderTableView(products, key)}
					</React.Fragment>
				);
			});
		} else {
			return renderTableView(productList);
		}
	};

	const getRemainingDays = (requiredDate) => {
		let dueDay = getDateDifference(
			moment().format('YYYY-MM-DD'),
			onGetLocalFromUtc(requiredDate),
			'days'
		);
		return (
			<span className="regular-12 red ml-2">
				({dueDay} {Math.abs(dueDay) > 1 ? 'Days' : 'Day'})
			</span>
		);
	};

	const renderProductStatusWiseRequireDate = (product, item) => {
		return (
			<>
				<div className="mb-1">
					<CustomTooltip text={item?.status}>
						<span
							className={`status ${getProductStatusWiseColor[item?.status] || 'purple-1'}`}
							key={`status_${item?.status}`}
						>
							{item?.status?.length > 40 ? `${item?.status?.slice(0, 40)}...` : item?.status}
						</span>
					</CustomTooltip>
				</div>
				{item?.date && (
					<>
						<p className="gray_dark_02 regular-12 my-1 text-nowrap">
							{item.entity === 'PO' ? 'ETD' : 'Required Date'}
							<span className="gray_dark_01 weight-500 ml-1">
								{changeDateFormat(item.date, 'YYYY-MM-DD', 'DD/MM/YYYY')}
								{getRemainingDays(item.date)}
							</span>
						</p>
					</>
				)}
			</>
		);
	};

	const getProductsWitBasicInfoUpdated = (products = []) => {
		return (
			products
				?.filter((product) =>
					product?.setPackStyleFlag === 'SET_STYLE'
						? product?.setMemberProducts?.some((member) => member?.isBasicInfoUpdated)
						: product?.isBasicInfoUpdated
				)
				?.map((item) => item.id) || []
		);
	};

	const isBasicInfoUpdated = (product) => {
		if (product?.setPackStyleFlag === 'SET_STYLE') {
			return product?.setMemberProducts?.some((member) => member?.isBasicInfoUpdated) || false;
		}
		return product?.isBasicInfoUpdated || false;
	};

	const renderProductRow = (product, rowSpanNo = null, rowIndex) => {
		rowSpanNo = rowIndex === 0 ? rowSpanNo : null;
		return (
			<tr>
				{rowIndex === 0 && (
					<td rowSpan={rowSpanNo} className="select-box-column">
						{isBasicInfoUpdated(product) && (
							<div className="custom-chekbox">
								<div className="form-group m-0">
									<input
										type="checkbox"
										id={product.id}
										name="productId"
										value={product.id}
										onChange={() => toggleSelect(product.id)}
										checked={!!product.isSelected}
									/>
									<label htmlFor={product.id} />
								</div>
							</div>
						)}
					</td>
				)}
				{rowIndex === 0 && (
					<td rowSpan={rowSpanNo} className="style-details-column">
						<div
							className="d-flex align-items-center"
							onClick={() => {
								setDesignIdToView(product);
							}}
						>
							{!product?.isSet && (
								<img
									src={product?.designDocuments?.[0]?.docUrl}
									alt=""
									className="cell-img pr-2"
								/>
							)}
							{product?.isSet && renderSetImages(product?.designDocuments, '56px')}
							<div
								className="d-inline-block align-middle ml-1 cursor-pointer"
								data-toggle="modal"
								data-target="#designViewModal"
							>
								{product.isSet && <span className="status set">Set</span>}
								{product.isPack && <span className="status pack">Pack</span>}
								<p className="regular-12 weight-500 gray_dark_01 my-1">
									{product?.referenceNumber}
								</p>
								<ul className="common-list-component mb-1">
									{!product.isSet && product?.productGroup && (
										<li>{product?.productGroup}</li>
									)}
									{product.isSet && product?.setMemberProducts?.[0]?.productGroup && (
										<li>{product?.setMemberProducts?.[0]?.productGroup}</li>
									)}
									{!product.isSet && product?.subCategory && (
										<li>
											<Tooltip title={product?.subCategory} placement="top" arrow>
												<span>{getShortName(product?.subCategory, 25)}</span>
											</Tooltip>
										</li>
									)}
									{product.isSet && <SetStyleSubCategory product={product} />}
									{product.addedByName && (
										<li>
											<Tooltip
												title={
													<span className="regular-14">{product.addedByName}</span>
												}
												placement="top"
												arrow
											>
												<span>{getShortName(product.addedByName, 10)}</span>
											</Tooltip>
										</li>
									)}
								</ul>
							</div>
						</div>
					</td>
				)}
				{rowIndex === 0 && (
					<td rowSpan={rowSpanNo} className="status-date-column">
						{product?.productStatusList?.map((status) =>
							renderProductStatusWiseRequireDate(product, status)
						)}
					</td>
				)}
				<td className="trims">
					<ul className="with-bullet">
						{Object.keys(product?.fabricResponse)?.length > 0 &&
							product?.fabricResponse?.[Object.keys(product?.fabricResponse)[rowIndex]]?.map(
								(item) =>
									item.name && (
										<li key={`fabric_${item.id}`} className="regular-12 gray_dark_02">
											{item.name}
										</li>
									)
							)}
						{!product.isSet && product?.fabricName && (
							<li className="regular-12 gray_dark_02">{product.fabricName}</li>
						)}
					</ul>
				</td>
				<td className="fabric">
					<ul className="with-bullet">
						{Object.keys(product?.trimResponse)?.length > 0 &&
							product?.trimResponse?.[Object.keys(product?.trimResponse)[rowIndex]]?.map(
								(item) =>
									item.name && (
										<li key={`fabric_${item.id}`} className="regular-12 gray_dark_02">
											<CustomTooltip
												placement="top"
												arrowCenter={true}
												text={item.name}
												className={item.name.length > 120 ? 'show' : 'hide'}
											>
												{getShortName(item.name, 120)}
											</CustomTooltip>
										</li>
									)
							)}
					</ul>
				</td>
				{rowIndex === 0 && (
					<td rowSpan={rowSpanNo} className="tag">
						<CollectionTableTags
							product={product}
							productWiseTags={productWiseTags}
							fetchAllTags={fetchAllTags}
							updateTagList={updateTagList}
						/>
					</td>
				)}
				{rowIndex === 0 && (
					<td rowSpan={rowSpanNo}>
						<ProductRemarks product={product} />
					</td>
				)}
			</tr>
		);
	};

	const renderFabricResponse = (product) => {
		let fabrics = {};
		if (product.isSet) {
			fabrics = groupByKey(product?.mainFabricResponses || [], 'productId');
		}
		return fabrics;
	};

	const renderProducts = (products) => {
		return products?.map((product, index) => {
			const modifiedProduct = {
				...product,
				fabricResponse: renderFabricResponse(product),
				trimResponse: groupByKey(product?.trimsResponses || [], 'productId')
			};
			const mergedRowLength = product.isSet ? product.setMemberProductIds.length : 1;
			const rowSpanNo = product.isSet ? product.setMemberProductIds.length : null;

			return (
				<React.Fragment key={`product_${index}`}>
					{Array.from({ length: mergedRowLength }).map((_, rowIndex) => (
						<React.Fragment key={`product_${index}_${rowIndex}`}>
							{renderProductRow(modifiedProduct, rowSpanNo, rowIndex)}
						</React.Fragment>
					))}
				</React.Fragment>
			);
		});
	};

	const getGroupWiseSelectedIds = (products = []) => {
		let selectedIds = [];
		products.forEach((item) => {
			if (selectedProductIds.includes(item.id)) {
				selectedIds.push(item.id);
			}
		});
		return selectedIds;
	};

	const renderTableView = (products) => {
		let selectedIds = getGroupWiseSelectedIds(products);
		let newId = `sampleAllSelect_${_getKey()}`;
		return (
			<table className="table with-border no-hover">
				<thead>
					<tr>
						<th className="select-box-column">
							<div className="custom-chekbox">
								<div className="form-group m-0">
									<input
										type="checkbox"
										name={newId}
										id={newId}
										onChange={(e) => onSelectAll(e, products)}
										checked={
											products.length > 0 &&
											getProductsWitBasicInfoUpdated(products).length ===
												selectedIds.length
										}
									/>
									<label htmlFor={newId} />
								</div>
							</div>
						</th>
						<th>Design</th>
						<th>Status &amp; Date</th>
						<th>Fabric</th>
						<th>Trims</th>
						<th>Tag</th>
						<th>Remark</th>
					</tr>
				</thead>
				<tbody>{renderProducts(products)}</tbody>
			</table>
		);
	};

	return (
		<div className="collection-details-table-view mb-4 scroll-x-label">{renderGroupWiseTableView()}</div>
	);
};

export default CollectionDetailsTableView;
